import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search } from 'lucide-react'
import Flags from 'react-phone-number-input/flags'
import { ALL_COUNTRIES } from '../../lib/countries'
import { useListboxNav } from '../../lib/use-listbox-nav'

/** Country dropdown — full ~245-country picker with flags + type-to-search,
 *  matching the phone-number country picker style elsewhere in the app.
 *
 *  Why a custom popover instead of a native `<select>`: the SVG flag glyphs
 *  can't render inside `<option>` elements (the browser-native dropdown is
 *  text-only). So we render a button trigger + a click-outside popover with
 *  search-as-you-type + a scrollable list.
 *
 *  `value` is either the `clearValue` sentinel (default `'all'`) or an ISO
 *  alpha-2 code (e.g. `'IN'`). `onChange(next)` receives the same shape.
 *  ESC / outside-click dismiss; the search input auto-focuses on open.
 *
 *  Works both as a list FILTER (the default) and as a FORM field via props:
 *   - `placeholder` — trigger label when nothing is selected.
 *   - `clearLabel`  — label of the top reset row; pass `null` to hide it
 *                     entirely (a required field with no "clear").
 *   - `clearValue`  — value emitted by that reset row (forms use `''`).
 *   - `fullWidth`   — block, full-width sizing to sit in a form column.
 *   - `portal`      — render the popover through a portal to <body> with
 *                     fixed positioning, so it floats ABOVE an overflow-
 *                     clipping ancestor (e.g. a Modal's scrollable body).
 *                     Use inside dialogs; leave off for normal-page filters.
 *   - `id` / `ariaLabel` — associate a `<label htmlFor>` / a11y name. */
export function CountryFilter({
  value,
  onChange,
  placeholder = 'All countries',
  clearLabel = 'All countries',
  clearValue = 'all',
  fullWidth = false,
  portal = false,
  id,
  ariaLabel = 'Country',
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [rect, setRect] = useState(null)
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const searchRef = useRef(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return ALL_COUNTRIES
    // Match against name (case-insensitive substring) OR dial code with an
    // optional leading '+'. So "ind" finds India, "+91" also finds India,
    // "uni" finds United Arab Emirates + United Kingdom + United States.
    const numericQ = q.replace(/^\+/, '')
    return ALL_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.dial && c.dial.includes(numericQ)),
    )
  }, [query])

  // Navigable rows for arrow-key nav: the reset row (only while not searching
  // AND when a clearLabel is configured) followed by the filtered list.
  const showClear = Boolean(clearLabel)
  const navCodes = useMemo(
    () =>
      (query.trim() || !showClear ? [] : [clearValue]).concat(
        filtered.map((c) => c.code),
      ),
    [filtered, query, showClear, clearValue],
  )
  const { highlightedIndex, setHighlightedIndex, optionRefs, onKeyDown } =
    useListboxNav({
      open,
      count: navCodes.length,
      onEnter: (i) => {
        const code = navCodes[i]
        if (code) pick(code)
      },
    })

  // Auto-focus the search box when the panel opens (a tick after mount).
  useEffect(() => {
    if (!open) return undefined
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  // Portal mode: track the trigger's viewport rect so the fixed-position
  // panel stays anchored to it across scroll / resize.
  const reposition = useCallback(() => {
    const el = triggerRef.current
    if (el) setRect(el.getBoundingClientRect())
  }, [])
  useEffect(() => {
    if (!open || !portal) return undefined
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, portal, reposition])

  // Outside-click + ESC close. mousedown (not click) so dismissal beats the
  // next click. The panel may live in a portal, so check both refs.
  useEffect(() => {
    if (!open) return undefined
    function onDocPointerDown(e) {
      const t = e.target
      if (
        !containerRef.current?.contains(t) &&
        !panelRef.current?.contains(t)
      ) {
        setOpen(false)
        setQuery('')
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        // Stop here (capture phase) so closing the popover doesn't also
        // close a surrounding Modal on the same Escape — the Modal listens
        // on `window`, which this pre-empts.
        e.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  function pick(code) {
    onChange(code)
    setOpen(false)
    setQuery('')
  }

  const selected =
    value && value !== clearValue
      ? ALL_COUNTRIES.find((c) => c.code === value) ?? null
      : null
  const SelectedFlag = selected ? Flags[selected.code] : null

  // Fixed-position style for the portal panel (mirrors TimezoneSelect):
  // flip upward when there isn't room below, and cap height to the viewport.
  let panelStyle = null
  if (portal && rect) {
    const GAP = 4
    const MARGIN = 8
    const width = Math.max(rect.width, 256)
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN
    const spaceAbove = rect.top - MARGIN
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow
    panelStyle = {
      position: 'fixed',
      left: Math.max(
        MARGIN,
        Math.min(rect.left, window.innerWidth - width - MARGIN),
      ),
      width,
      maxHeight: Math.max(160, Math.min(360, openUp ? spaceAbove : spaceBelow)),
      zIndex: 1000,
    }
    if (openUp) panelStyle.bottom = window.innerHeight - rect.top + GAP
    else panelStyle.top = rect.bottom + GAP
  }

  const panelInner = (
    <>
      <div className="p-2 border-b border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <div className="relative">
          <Search
            size={14}
            aria-hidden="true"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
          />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setHighlightedIndex(0)
            }}
            onKeyDown={onKeyDown}
            placeholder="Search Country or +Code…"
            className={
              'w-full h-9 pl-8 pr-3 rounded-md border ' +
              'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] ' +
              'text-sm text-[color:var(--color-ink)] ' +
              'placeholder:text-[color:var(--color-ink-subtle)] ' +
              'outline-none focus:border-[color:var(--color-accent)]'
            }
          />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {!query.trim() && showClear ? (
          <button
            type="button"
            role="option"
            aria-selected={value === clearValue}
            onClick={() => pick(clearValue)}
            onMouseEnter={() => setHighlightedIndex(0)}
            className={
              'w-full flex items-center gap-3 px-3 py-2 text-sm text-left ' +
              'cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
              'border-b border-[color:var(--color-rule)] ' +
              (highlightedIndex === 0
                ? 'bg-[color:var(--color-accent-bg)] ring-1 ring-inset ring-[color:var(--color-accent)] text-[color:var(--color-ink)]'
                : value === clearValue
                  ? 'text-[color:var(--color-accent-strong)] font-medium'
                  : 'text-[color:var(--color-ink)]')
            }
          >
            <span className="w-6 text-center text-[color:var(--color-ink-subtle)]">
              ⊘
            </span>
            <span className="flex-1">{clearLabel}</span>
          </button>
        ) : null}
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-sm text-center text-[color:var(--color-ink-muted)]">
            No countries found.
          </div>
        ) : (
          filtered.map((c, j) => {
            const isSelected = c.code === value
            const RowFlag = Flags[c.code]
            // Offset by 1 for the reset row when it's shown.
            const navIndex = query.trim() || !showClear ? j : j + 1
            return (
              <button
                key={c.code}
                ref={(el) => {
                  optionRefs.current[navIndex] = el
                }}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => pick(c.code)}
                onMouseEnter={() => setHighlightedIndex(navIndex)}
                className={
                  'w-full flex items-center gap-3 px-3 py-2 text-sm text-left ' +
                  'cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                  (highlightedIndex === navIndex
                    ? 'bg-[color:var(--color-accent-bg)] ring-1 ring-inset ring-[color:var(--color-accent)]'
                    : isSelected
                      ? 'font-medium'
                      : '')
                }
              >
                {RowFlag ? (
                  <RowFlag
                    title={c.name}
                    style={{ width: 20, height: 14, flexShrink: 0 }}
                  />
                ) : (
                  <span className="w-5" />
                )}
                <span className="flex-1 truncate text-[color:var(--color-ink)]">
                  {c.name}
                </span>
                {c.dial ? (
                  <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                    +{c.dial}
                  </span>
                ) : null}
              </button>
            )
          })
        )}
      </div>
    </>
  )

  const panelShellBase =
    'rounded-md border border-[color:var(--color-rule)] ' +
    'bg-[color:var(--color-card)] shadow-lg overflow-hidden'

  return (
    <div
      ref={containerRef}
      className={'relative ' + (fullWidth ? 'block w-full' : 'inline-block')}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-label={ariaLabel}
        onClick={() => {
          if (!open) {
            // Seed the highlight to the current selection (or top) so arrow
            // nav starts there. Query is empty on open → full nav list.
            const idx = navCodes.indexOf(value)
            setHighlightedIndex(idx >= 0 ? idx : 0)
          }
          setOpen((v) => !v)
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={
          'inline-flex items-center gap-2 h-9 px-3 rounded-md border ' +
          'border-[color:var(--color-rule)] bg-[color:var(--color-card)] ' +
          'text-sm text-[color:var(--color-ink)] cursor-pointer ' +
          'hover:border-[color:var(--color-accent-border)] ' +
          'focus:outline-none focus-visible:border-[color:var(--color-accent)] ' +
          'transition-colors ' +
          (fullWidth ? 'w-full' : 'min-w-[150px]')
        }
      >
        {SelectedFlag ? (
          <SelectedFlag
            title={selected.name}
            style={{ width: 18, height: 12, flexShrink: 0 }}
          />
        ) : null}
        <span
          className={
            'flex-1 text-left truncate ' +
            // Placeholder (subtle) styling ONLY when the field is truly
            // unset — i.e. form usage with an empty value. On filter
            // surfaces the 'all' sentinel IS the active selection
            // ("All countries"), so it renders in normal ink like the
            // sibling "All statuses" / "All plans" selects — the muted
            // treatment made it look disabled/unset next to them
            // (QA-reported on the AI Usage / Storage panels).
            (selected || value === 'all'
              ? ''
              : 'text-[color:var(--color-ink-subtle)]')
          }
        >
          {selected ? selected.name : placeholder}
        </span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={
            'shrink-0 text-[color:var(--color-ink-muted)] transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>

      {open && portal && rect
        ? createPortal(
            <div
              ref={panelRef}
              role="listbox"
              style={panelStyle}
              className={'overflow-y-auto ' + panelShellBase}
            >
              {panelInner}
            </div>,
            document.body,
          )
        : null}

      {open && !portal ? (
        <div
          ref={panelRef}
          role="listbox"
          className={
            'absolute top-full left-0 mt-1 max-w-[calc(100vw-2rem)] z-50 ' +
            (fullWidth ? 'w-full min-w-[16rem] ' : 'w-72 ') +
            panelShellBase
          }
        >
          {panelInner}
        </div>
      ) : null}
    </div>
  )
}
